import React from 'react';
import { Reorder } from 'framer-motion';
import type { EvaluationItem, OrderingConfig, OptionDef } from '../../../../../types/course';
import FloatingLabelPill from '../shared/FloatingLabelPill';
import AnswerPill from '../shared/AnswerPill';
import SectionHeader from '../shared/SectionHeader';
import AddItemCTA from '../shared/AddItemCTA';
import TrashButton from '../shared/TrashButton';
import { uid } from '../shared/tokens';

interface ItemEditorProps {
  item: Extract<EvaluationItem, { type: 'ordering_sequence' }>;
  onChange: (patch: Partial<Extract<EvaluationItem, { type: 'ordering_sequence' }>>) => void;
  onDelete: () => void;
  readOnly?: boolean;
}

export default function OrderingSequenceItem({ item, onChange, onDelete, readOnly }: ItemEditorProps) {
  const { question, items } = item.config;

  const updateConfig = (patch: Partial<OrderingConfig>) => {
    onChange({ config: { ...item.config, ...patch } });
  };

  const updateItem = (id: string, text: string) => {
    updateConfig({ items: items.map((o) => (o.id === id ? { ...o, text } : o)) });
  };

  const removeItem = (id: string) => {
    if (items.length <= 2) return;
    updateConfig({ items: items.filter((o) => o.id !== id) });
  };

  const addItem = () => {
    const newItem: OptionDef = { id: uid('ord'), text: '' };
    updateConfig({ items: [...items, newItem] });
  };

  return (
    <div className="flex flex-col gap-[31px] w-full">
      <SectionHeader title="Ordering Sequence" />
      <FloatingLabelPill
        label="Question"
        value={question}
        onChange={(v) => updateConfig({ question: v })}
        placeholder="Put these items in the correct order"
        size="xl"
        readOnly={readOnly}
        trailingIcon={!readOnly && <TrashButton onClick={onDelete} ariaLabel="Delete question" />}
      />
      <p className="-mt-2 text-center text-xs text-text-disabled">
        Correct order is the order listed below. Drag to reorder.
      </p>
      <Reorder.Group
        axis="y"
        values={items}
        onReorder={(next) => updateConfig({ items: next as OptionDef[] })}
        className="flex flex-col gap-3"
      >
        {items.map((it, idx) => (
          <Reorder.Item
            key={it.id}
            value={it}
            className={readOnly ? 'cursor-default' : 'cursor-grab'}
          >
            <AnswerPill
              value={it.text}
              onChange={(v) => updateItem(it.id, v)}
              placeholder={`Item ${idx + 1}`}
              readOnly={readOnly}
              onRemove={!readOnly && items.length > 2 ? () => removeItem(it.id) : undefined}
              trailingLabel={`#${idx + 1}`}
              leadingIcon={
                <span aria-hidden="true" className="text-text-secondary">
                  ⋮⋮
                </span>
              }
            />
          </Reorder.Item>
        ))}
      </Reorder.Group>
      {!readOnly && <AddItemCTA label="Add Item" onClick={addItem} />}
    </div>
  );
}
